// C:\Users\aleja\Code\inventory_analytics\src\front\src\store\flux.js
import repairableSparePartActions from "./flux/repairableSparePartActions";
import authActions from "./flux/authActions";
import inventoryActions from "./flux/inventoryActions";
import userActions from "./flux/userActions";
import dashboardActions from "./flux/dashboardActions";
import userActivityActions from "./flux/userActivityActions";
import warehouseCostAnalyticsActions from "./flux/warehouseCostAnalyticsActions";
import pendingPartExitActions from "./flux/pendingPartExitActions";
import partDocumentActions from "./flux/partDocumentActions";
import machineCatalogActions from "./flux/machineCatalogActions";
import getApiBaseUrl from "../utils/api";

const API_URL = getApiBaseUrl();

const buildOrmaUrl = (path, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return `${API_URL}${path}${queryString ? `?${queryString}` : ""}`;
};

const getOrmaPayloadMessage = (payload, fallbackMessage) =>
  payload?.error || payload?.msg || fallbackMessage;

const fetchOrmaJson = async ({
  token,
  actions,
  url,
  options = {},
  fallbackMessage,
}) => {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      Authorization: "Bearer " + token,
      ...(options.headers || {}),
    },
  });

  if (response.status === 401) {
    await actions.handleApiError(response);
    return { success: false, data: null, error: "Request failed", status: 401 };
  }

  const payload = await response.json().catch(() => null);

  if (!response.ok || !payload?.success) {
    return {
      success: false,
      data: payload?.data || null,
      error: getOrmaPayloadMessage(payload, fallbackMessage),
      status: response.status,
    };
  }

  return {
    success: true,
    data: payload.data || null,
    error: null,
    status: response.status,
  };
};

const getState = ({ getStore, getActions, setStore }) => {
  return {
    store: {
      dashboard: null,
      inventoryHistory: [],
      userActivityLogs: [],
      warehouseCostAnalytics: null,
      warehouseCostAnalyticsLoading: false,
      warehouseCostAnalyticsError: null,
      dashboardExportLoading: false,
      dashboardExportError: null,
      suppliersCatalog: [],
      suppliersCatalogLoading: false,
      suppliersCatalogError: null,
      partsCatalog: [],
      partsCatalogLoading: false,
      partsCatalogError: null,
      partDetail: null,
      partDetailLoading: false,
      partDetailError: null,
      supplierDetail: null,
      supplierDetailLoading: false,
      supplierDetailError: null,
      supplierDocuments: [],
      supplierDocumentsLoading: false,
      supplierDocumentsError: null,
      supplierDocumentUploadLoading: false,
      supplierDocumentUploadError: null,
      supplierDocumentDownloadingId: null,
      supplierDocumentDeletingId: null,
      supplierDocumentActionError: null,
      pendingPartExits: [],
      pendingPartExitsLoading: false,
      pendingPartExitsError: null,

      controlledPartEntries: [],
      controlledPartEntriesLoading: false,
      controlledPartEntriesError: null,
      scannerSettings: {
        require_operator_identification: false,
        configured: false,
        updated_by: null,
        created_at: null,
        updated_at: null,
      },
      scannerSettingsLoading: false,
      scannerSettingsError: null,

      token: localStorage.getItem("token") || null,
      role: localStorage.getItem("role") || null,
      username: localStorage.getItem("username") || null,
      mustChangePassword: localStorage.getItem("must_change_password") === "true",

      isAuthenticated: localStorage.getItem("token") ? true : false,
      authResolved: false,

      toast: {
        message: null,
        type: "info",
      },
    },

    actions: {
      ...repairableSparePartActions({ getStore, getActions, setStore }),
      ...authActions({ getStore, getActions, setStore }),
      ...inventoryActions({ getStore, getActions, setStore }),
      ...userActions({ getStore, getActions, setStore }),
      ...dashboardActions({ getStore, getActions, setStore }),
      ...userActivityActions({ getStore, getActions, setStore }),
      ...warehouseCostAnalyticsActions({ getStore, getActions, setStore }),
      ...pendingPartExitActions({ getStore, getActions, setStore }),
      ...partDocumentActions({ getStore, getActions, setStore }),
      ...machineCatalogActions({ getStore, getActions, setStore }),

      getOrmaCatalogMeta: async (options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-catalog/meta"),
            options,
            fallbackMessage: "No se pudo cargar la frescura del catálogo ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA catalog meta error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo cargar la frescura del catálogo ORMA",
          };
        }
      },

      searchOrmaCatalog: async (params = {}, options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-catalog/search", params),
            options,
            fallbackMessage: "No se pudo buscar en el catálogo ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA catalog search error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo buscar en el catálogo ORMA",
          };
        }
      },


      exportOrmaCatalog: async (params = {}) => {
        try {
          const { token } = getStore();
          const response = await fetch(
            buildOrmaUrl("/api/catalog/orma-catalog/export.xlsx", params),
            {
              cache: "no-store",
              headers: {
                Authorization: "Bearer " + token,
              },
            },
          );

          if (response.status === 401) {
            await getActions().handleApiError(response);
            return {
              success: false,
              error: "La sesión ha caducado",
              status: 401,
            };
          }

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            return {
              success: false,
              error: getOrmaPayloadMessage(
                payload,
                "No se pudo exportar el catálogo ORMA",
              ),
              status: response.status,
            };
          }

          const blob = await response.blob();
          const disposition =
            response.headers.get("Content-Disposition") || "";
          const utf8Filename = disposition.match(
            /filename\*=UTF-8''([^;]+)/i,
          );
          const plainFilename = disposition.match(
            /filename="?([^";]+)"?/i,
          );
          const filename = decodeURIComponent(
            utf8Filename?.[1] ||
              plainFilename?.[1] ||
              "catalogo_orma_filtrado.xlsx",
          );

          const objectUrl = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.setTimeout(() => {
            window.URL.revokeObjectURL(objectUrl);
          }, 0);

          return {
            success: true,
            data: {
              filename,
              size: blob.size,
            },
            error: null,
            status: response.status,
          };
        } catch (error) {
          console.error("ORMA catalog export error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo exportar el catálogo ORMA",
            status: 0,
          };
        }
      },


      exportSelectedOrmaCatalog: async (items = []) => {
        try {
          const { token } = getStore();
          const response = await fetch(
            buildOrmaUrl(
              "/api/catalog/orma-catalog/export-selected.xlsx",
            ),
            {
              method: "POST",
              cache: "no-store",
              headers: {
                Authorization: "Bearer " + token,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ items }),
            },
          );

          if (response.status === 401) {
            await getActions().handleApiError(response);
            return { success: false, error: "La sesión ha caducado", status: 401 };
          }

          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            return {
              success: false,
              error: getOrmaPayloadMessage(
                payload,
                "No se pudieron exportar los elementos guardados",
              ),
              status: response.status,
            };
          }

          const blob = await response.blob();
          const disposition = response.headers.get("Content-Disposition") || "";
          const utf8Filename = disposition.match(/filename\*=UTF-8''([^;]+)/i);
          const plainFilename = disposition.match(/filename="?([^";]+)"?/i);
          const filename = decodeURIComponent(
            utf8Filename?.[1] || plainFilename?.[1] || "elementos_guardados_orma.xlsx",
          );
          const objectUrl = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
          return { success: true, data: { filename, size: blob.size }, status: response.status };
        } catch (error) {
          console.error("ORMA selected export error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudieron exportar los elementos guardados",
            status: 0,
          };
        }
      },

      getOrmaCatalogFacets: async (params = {}, options = {}) => {
        try {
          const { token } = getStore();
          const result = await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-catalog/facets", params),
            options,
            fallbackMessage: "No se pudieron cargar los filtros del catálogo ORMA",
          });

          if (!result.success) return result;

          return {
            ...result,
            data: result.data || {},
          };
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA catalog facets error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudieron cargar los filtros del catálogo ORMA",
          };
        }
      },

      getOrmaCatalogMaterial: async (materialId, options = {}) => {
        const normalizedMaterialId = String(materialId || "").trim();

        if (!normalizedMaterialId) {
          return { success: false, data: null, error: "Material inválido" };
        }

        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl(
              `/api/catalog/orma-catalog/material/${encodeURIComponent(normalizedMaterialId)}`,
            ),
            options,
            fallbackMessage: "No se pudo cargar el material ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA catalog material error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo cargar el material ORMA",
          };
        }
      },

      importOrmaCatalog: async (formData, options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-catalog/import"),
            options: {
              ...options,
              method: "POST",
              body: formData,
            },
            fallbackMessage: "No se pudo importar el catálogo ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA catalog import error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo importar el catálogo ORMA",
          };
        }
      },

      getOrmaValuationOptions: async (options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-valuation/options"),
            options,
            fallbackMessage: "No se pudieron cargar las opciones de valoración ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA valuation options error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudieron cargar las opciones de valoración ORMA",
            status: 0,
          };
        }
      },

      calculateOrmaWarehouseValuation: async (config, options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-valuation/calculate"),
            options: {
              ...options,
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(options.headers || {}),
              },
              body: JSON.stringify({ config }),
            },
            fallbackMessage: "No se pudo calcular la valoración del almacén",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA warehouse valuation calculation error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo calcular la valoración del almacén",
            status: 0,
          };
        }
      },

      getOrmaWarehouseValuationDetails: async (payload = {}, options = {}) => {
        try {
          const { token } = getStore();
          return await fetchOrmaJson({
            token,
            actions: getActions(),
            url: buildOrmaUrl("/api/catalog/orma-valuation/details"),
            options: {
              ...options,
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(options.headers || {}),
              },
              body: JSON.stringify(payload),
            },
            fallbackMessage: "No se pudo cargar el detalle de la valoración ORMA",
          });
        } catch (error) {
          if (error.name === "AbortError") {
            return { success: false, data: null, error: "Request aborted", aborted: true };
          }

          console.error("ORMA warehouse valuation details error:", error);
          return {
            success: false,
            data: null,
            error: "No se pudo cargar el detalle de la valoración ORMA",
            status: 0,
          };
        }
      },

      showToast: (message, type = "info") => {
        setStore({
          toast: {
            message: message,
            type: type,
          },
        });
      },

      clearToast: () => {
        setStore({
          toast: {
            message: null,
            type: "info",
          },
        });
      },
    },
  };
};

export default getState;
